
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Link from '@/components/compat/Link';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { SiteRulesSection } from '@/lib/cms-types';
import { painelSurfaceClass, painelPageBackButtonClass, painelSwitchClass } from '@/components/painel/painel-styles';
import { useCms } from '@/components/painel/cms/cms-context';
import { RichTextEditor } from '@/components/painel/cms/RichTextEditor';
import {
  painelDialogContentClass,
  painelInputClass,
} from '@/components/painel/cms/painel-form';
import { painelButtonPrimaryClass, painelButtonOutlineClass } from '@/components/painel/painel-styles';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { usePainelText } from '@/components/painel/painel-texts-context';

export function PainelRulesEditorPage() {
  const location = useLocation();
  const mode = location.pathname.includes('/conduta') ? 'conduta' : 'regras';
  const pageText = usePainelText(mode === 'conduta' ? 'pagina_conduta' : 'pagina_regras');
  const { showSaveSuccess } = usePainelSaveFeedback();
  const { site, setSite, setRulesSections, setConduct, hydrated } = useCms();
  const [sectionDialog, setSectionDialog] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [secTitle, setSecTitle] = useState('');
  const [secCat, setSecCat] = useState('');
  const [secUpdated, setSecUpdated] = useState(false);
  const [secBody, setSecBody] = useState('');
  const [deleteRuleId, setDeleteRuleId] = useState<string | null>(null);

  const orderedRules = [...site.rulesSections].sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  const openNewSection = () => {
    setEditingSectionId(null);
    setSecTitle('');
    setSecCat('Comportamento');
    setSecUpdated(false);
    setSecBody('<p></p>');
    setSectionDialog(true);
  };

  const openEditSection = (s: SiteRulesSection) => {
    setEditingSectionId(s.id);
    setSecTitle(s.title);
    setSecCat(s.category);
    setSecUpdated(s.updated);
    setSecBody(s.bodyHtml || '<p></p>');
    setSectionDialog(true);
  };

  const saveSection = () => {
    const title = secTitle.trim();
    if (!title) return;
    const category = secCat.trim() || 'Comportamento';
    const bodyHtml = secBody.trim() || '<p></p>';
    if (editingSectionId) {
      setRulesSections(
        site.rulesSections.map((x) =>
          x.id === editingSectionId
            ? { ...x, title, category, updated: secUpdated, bodyHtml }
            : x
        )
      );
    } else {
      const nextOrder =
        site.rulesSections.reduce((m, x) => Math.max(m, x.displayOrder), 0) + 1;
      setRulesSections([
        ...site.rulesSections,
        {
          id: crypto.randomUUID(),
          displayOrder: nextOrder,
          title,
          category,
          updated: secUpdated,
          bodyHtml,
        },
      ]);
    }
    setSectionDialog(false);
    showSaveSuccess({
      title: editingSectionId ? 'Secção actualizada' : 'Secção criada',
      description: 'As regras na página pública foram actualizadas.',
    });
  };

  const confirmDeleteRule = () => {
    if (deleteRuleId) {
      setRulesSections(site.rulesSections.filter((x) => x.id !== deleteRuleId));
    }
    setDeleteRuleId(null);
  };

  const moveSection = (id: string, dir: -1 | 1) => {
    const list = [...orderedRules];
    const idx = list.findIndex((x) => x.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= list.length) return;
    const copy = [...list];
    const [row] = copy.splice(idx, 1);
    copy.splice(j, 0, row);
    setRulesSections(copy.map((s, i) => ({ ...s, displayOrder: i + 1 })));
  };

  const conduct = site.conduct;

  const addConductItem = () => {
    setConduct({
      ...conduct,
      items: [...conduct.items, { id: crypto.randomUUID(), bodyHtml: '<p></p>' }],
    });
  };

  return (
    <div className="space-y-10">
      <div>
        <Button
          variant="ghost"
          asChild
          className={painelPageBackButtonClass}
        >
          <Link href="/painel/paginas">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Páginas
          </Link>
        </Button>
        <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
          {pageText.title || (mode === 'conduta' ? 'Código de conduta' : 'Regras do xat')}
        </h1>
        <p className="mt-1 font-inter text-sm text-slate-400">
          {pageText.description ||
            (mode === 'conduta'
              ? 'Edite o conteúdo público de /conduta.'
              : 'Edite o conteúdo público de /regras.')}
        </p>
      </div>

      {mode === 'regras' ? (
        <>
      <section
        className={cn(
          painelSurfaceClass,
          'space-y-4 p-6 md:p-8',
          !hydrated && 'opacity-70'
        )}
      >
        <h2 className="font-poppins text-lg font-semibold text-white">Cabeçalho da página</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="grid gap-2 md:col-span-2">
            <Label className="text-blue-100/90">Título</Label>
            <input
              value={site.rulesPageTitle}
              onChange={(e) => setSite({ ...site, rulesPageTitle: e.target.value })}
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label className="text-blue-100/90">Descrição</Label>
            <textarea
              value={site.rulesPageSubtitle}
              onChange={(e) => setSite({ ...site, rulesPageSubtitle: e.target.value })}
              className={cn(painelInputClass, 'min-h-[80px] resize-y py-2')}
            />
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="font-poppins text-lg font-semibold text-white">Seções de regras</h2>
          <Button
            type="button"
            onClick={openNewSection}
            className="rounded-xl border border-sky-400/40 bg-sky-500/90 text-white hover:bg-sky-400"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nova seção
          </Button>
        </div>

        <div className="space-y-2">
          {orderedRules.length === 0 ? (
            <p className="rounded-xl border border-dashed border-white/15 px-4 py-10 text-center text-sm text-slate-400">
              Nenhuma seção. Adicione a primeira.
            </p>
          ) : (
            orderedRules.map((s, i) => (
              <div
                key={s.id}
                className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="font-poppins text-sm font-semibold text-blue-50">
                    <span className="text-slate-400">{String(s.displayOrder).padStart(2, '0')}.</span>{' '}
                    {s.title}
                  </p>
                  <p className="truncate text-xs text-slate-400">{s.category}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="rounded-xl text-blue-100"
                    disabled={i === 0}
                    onClick={() => moveSection(s.id, -1)}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="rounded-xl text-blue-100"
                    disabled={i === orderedRules.length - 1}
                    onClick={() => moveSection(s.id, 1)}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="rounded-xl text-blue-100"
                    onClick={() => openEditSection(s)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="rounded-xl text-rose-300"
                    onClick={() => setDeleteRuleId(s.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
        </>
      ) : null}

      {mode === 'conduta' ? (
      <section className={cn(painelSurfaceClass, 'space-y-5 p-6 md:p-8')}>
        <h2 className="font-poppins text-lg font-semibold text-white">Código de conduta</h2>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="grid gap-2">
            <Label className="text-blue-100/90">Etiqueta (badge)</Label>
            <input
              value={conduct.badgeLabel}
              onChange={(e) =>
                setConduct({ ...conduct, badgeLabel: e.target.value })
              }
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label className="text-blue-100/90">Título</Label>
            <input
              value={conduct.title}
              onChange={(e) => setConduct({ ...conduct, title: e.target.value })}
              className={painelInputClass}
            />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label className="text-blue-100/90">Subtítulo</Label>
            <textarea
              value={conduct.subtitle}
              onChange={(e) => setConduct({ ...conduct, subtitle: e.target.value })}
              className={cn(painelInputClass, 'min-h-[72px] resize-y py-2')}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-blue-100/90">Itens</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl border-white/15 text-blue-100"
              onClick={addConductItem}
            >
              <Plus className="mr-1 h-4 w-4" />
              Item
            </Button>
          </div>
          {conduct.items.map((it, idx) => (
            <div key={it.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-400">Item {idx + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-rose-300"
                  onClick={() =>
                    setConduct({
                      ...conduct,
                      items: conduct.items.filter((x) => x.id !== it.id),
                    })
                  }
                >
                  Remover
                </Button>
              </div>
              <RichTextEditor
                value={it.bodyHtml}
                onChange={(html) =>
                  setConduct({
                    ...conduct,
                    items: conduct.items.map((x) =>
                      x.id === it.id ? { ...x, bodyHtml: html } : x
                    ),
                  })
                }
                resetKey={it.id}
              />
            </div>
          ))}
        </div>
      </section>
      ) : null}

      <Dialog open={sectionDialog} onOpenChange={setSectionDialog}>
        <DialogContent className={cn(painelDialogContentClass, 'max-w-lg gap-4')}>
          <DialogHeader>
            <DialogTitle className="font-poppins text-lg text-white">
              {editingSectionId ? 'Editar seção' : 'Nova seção'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Título</Label>
              <input
                value={secTitle}
                onChange={(e) => setSecTitle(e.target.value)}
                className={painelInputClass}
              />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Categoria (filtro)</Label>
              <input
                value={secCat}
                onChange={(e) => setSecCat(e.target.value)}
                className={painelInputClass}
                placeholder="Comportamento, Segurança…"
              />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
              <Label className="text-blue-100/90">Marcar como atualizada</Label>
              <Switch checked={secUpdated} onCheckedChange={setSecUpdated} className={painelSwitchClass} />
            </div>
            <div className="grid gap-2">
              <Label className="text-blue-100/90">Conteúdo</Label>
              <RichTextEditor
                value={secBody}
                onChange={setSecBody}
                resetKey={editingSectionId ?? 'new-section'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className={painelButtonOutlineClass}
              onClick={() => setSectionDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              className={painelButtonPrimaryClass}
              onClick={saveSection}
              disabled={!secTitle.trim()}
            >
              Salvar seção
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PainelConfirmDialog
        open={deleteRuleId !== null}
        onOpenChange={(o) => !o && setDeleteRuleId(null)}
        title="Excluir seção?"
        description="Esta regra deixará de aparecer no site público."
        confirmLabel="Excluir"
        onConfirm={confirmDeleteRule}
      />
    </div>
  );
}
